import type { RowData } from '@tanstack/react-table';

import type { DataTableColumnDef, DataTableRow } from './features';

/** Reads an accessor key the way TanStack does: a dotted key walks nested objects. */
function readAccessorKey(row: unknown, accessorKey: string): unknown {
    const path = accessorKey.includes('.') ? accessorKey.split('.') : [accessorKey];
    let value: unknown = row;
    for (const key of path) {
        if (value === null || typeof value !== 'object') return undefined;
        value = Reflect.get(value, key);
    }
    return value;
}

/**
 * The columns with every null cell value read as undefined. TanStack keeps undefined values
 * away from the sort function and places them by `sortUndefined`, which useDataTable sets to
 * 'last'. A null would reach the sort function instead and land wherever it compares, which
 * for a date is the middle of the list. Cells render null and undefined alike.
 */
export function withEmptyValuesUndefined<T extends RowData>(
    columns: readonly DataTableColumnDef<T>[]
): DataTableColumnDef<T>[] {
    return columns.map((column) => {
        if ('columns' in column && column.columns) {
            return { ...column, columns: withEmptyValuesUndefined(column.columns) };
        }
        if ('accessorFn' in column && column.accessorFn) {
            const read = column.accessorFn;
            return {
                ...column,
                accessorFn: (row: T, index: number) => read(row, index) ?? undefined,
            };
        }
        if ('accessorKey' in column && column.accessorKey !== undefined) {
            const key = String(column.accessorKey);
            return { ...column, accessorFn: (row: T) => readAccessorKey(row, key) ?? undefined };
        }
        return column;
    });
}

function toTime(value: unknown): number {
    let time = Number.NaN;
    if (value instanceof Date) time = value.getTime();
    else if (typeof value === 'string' || typeof value === 'number')
        time = new Date(value).getTime();
    // An unreadable date sorts as the oldest, so the comparison stays consistent.
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * Orders a column of timestamps (ISO strings or Dates) by time. Empty values never get here;
 * the table puts them last first.
 */
export function timeSortFn<T extends RowData>(
    rowA: DataTableRow<T>,
    rowB: DataTableRow<T>,
    columnId: string
): number {
    const a = toTime(rowA.getValue(columnId));
    const b = toTime(rowB.getValue(columnId));
    if (a === b) return 0;
    return a < b ? -1 : 1;
}
